import useAccountManagementStore from "../../stores/accountManagementStore";
import useDataStore from "../../stores/dataStore";
import { MergeResult, MergeTableData } from "../mergeTableData";
import getTableData from "./table_database/getTableData";
import upsertTableData from "./table_database/upsertTableData";
import debounce from "../../utility/debounce";
import updateTextFilesCloud from "../updateTextFilesCloud";
import removeDataFromCloud from "../removeDataFromCloud";

// Store the last parameters in module scope
let lastParams: { mergeResult?: MergeResult } = {};
let lastResolve: ((value: any) => void) | null = null;
let lastReject: ((reason?: any) => void) | null = null;

// Create debounced function once, outside the main function
const debouncedSync = Object.assign(
    debounce(() => {
        syncChanges(lastParams.mergeResult)
            .then((result) => {
                if (lastResolve) lastResolve(result);
            })
            .catch((error) => {
                if (lastReject) lastReject(error);
            });
        lastResolve = null;
        lastReject = null;
    }, 5000),
    { cancel: () => {} }, // Placeholder; actual cancel depends on debounce implementation
);

/**
 * Sync the local data with the cloud data.
 * If passedMergeResult is defined, it will use that instead of getting the data from the store.
 * If skipDebounce is true, it will not debounce the function. (used on logout)
 * @param {MergeResult | undefined} passedMergeResult - The result of the merge operation.
 * @param {boolean} skipDebounce - Whether to debounce the function or not.
 * @returns {Promise<any>} - Promise that resolves with the merge result data
 */
export default async function syncChangesToCloud(
    passedMergeResult?: MergeResult,
    skipDebounce = false,
) {
    lastParams = { mergeResult: passedMergeResult };

    if (!skipDebounce) {
        return new Promise((resolve, reject) => {
            lastResolve = resolve;
            lastReject = reject;
            debouncedSync();
        });
    }

    // Cancel any pending debounced sync before proceeding directly
    if (typeof debouncedSync.cancel === "function") {
        debouncedSync.cancel();
    }

    return await syncChanges(passedMergeResult);
}

async function syncChanges(passedMergeResult?: MergeResult) {
    const userData = useAccountManagementStore.getState().userData;

    // If user is not logged in, return
    if (!userData.id) return;

    // If passedMergeResult is defined, use it
    if (passedMergeResult) {
        const conflicts = passedMergeResult.conflicts;

        if (conflicts) {
            upsertTableData(conflicts.localNewer);
            removeDataFromCloud(passedMergeResult.removeFromCloud);
            updateTextFilesCloud(passedMergeResult);
        }
    } else {
        // Get cloud data
        const mergeData = await GetMergeData();

        if (mergeData.conflicts) {
            upsertTableData(mergeData.conflicts.localNewer);
            removeDataFromCloud(mergeData.removeFromCloud);
            updateTextFilesCloud(mergeData);
        }
    }
}

function GetMergeData() {
    return getTableData().then((cloudTableData) => {
        if (cloudTableData === "INVALID_SESSION") return;

        // Get local data
        const localTableData = useDataStore.getState().data;

        // Merge cloud and local data
        const mergeResult = MergeTableData(localTableData, cloudTableData);
        return mergeResult;
    });
}
